import React, { useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

// Components
import Button from '../Button'
import Text from '../Text'
import { Icon, IconWrapper } from '../icon'

// Types
import { IconType } from '../icon/IconType'

// Utils
import { ITheme, useAppState } from '../../context/AppContext'
import { useStickyNavOffset } from '../../hooks/useStickyNavOffset'

// Styles
import { shadows, fastTransition } from '../../styles/effects'
import { applyTextStyle } from '../../styles/typography'
import { hsla } from '../../styles/colors'
import { mqTo } from '../../styles/mediaQueries'

export type TableSortType = 'text' | 'number' | 'date'
export type TableSortDirection = 'asc' | 'desc'
type TableSortValue = string | number | Date | null | undefined

export interface ITableHeader {
  label: string
  /** Makes the column sortable; rows compare their `sortValues` entry as this type */
  sort?: TableSortType
  /** Right-align a labelled column that holds buttons or a chevron, like Details */
  align?: 'end'
}

export interface ITableEmptyState {
  /** Defaults to Search, or List when there is no data at all */
  icon?: IconType
  title: string
  text?: string
  action?: { caption: string; onClick: () => void }
  /** Nothing has been made yet, so the column headers are left out too */
  noData?: boolean
}

export interface ITableSort {
  column: number
  direction: TableSortDirection
}

interface IProps {
  className?: string
  columnWidths?: string[]
  mainHeaders: Array<string | ITableHeader>
  /** A light second header row with small captions, one per column */
  headerCaptions?: React.ReactNode[]
  defaultSort?: ITableSort
  /** Expandable tables: tinted main rows with white sub-rows under them */
  variant?: 'grouped'
  /** Tighter rows, for tables inside modals */
  compact?: boolean
  /** Keep every column on small screens instead of only the first and last */
  allColumnsOnMobile?: boolean
  /** Skeleton rows in the shape of the columns while the data loads */
  loading?: boolean
  /** Shown inside the table when there are no rows */
  emptyState?: ITableEmptyState
  mainData: Array<{
    /** Stable row identity, so an expanded row stays open when the order changes */
    id?: string
    /** One value per column, read by sortable headers. Empty values always sort last. */
    sortValues?: TableSortValue[]
    onClick?: () => void
    openByDefault?: boolean
    hoverColor?: string
    elements: React.ReactNode[]
    subTable?: {
      subHeaders: React.ReactNode[]
      subElements: Array<{
        onClick?: () => void
        data: React.ReactNode[]
        /** Handle for the e2e suite: a sub-row is a whole record's row, and
            every cell in it is translated or formatted copy. */
        'data-cy'?: string
      }>
      customExpandIcon?: boolean
    }
  }>
}

const collator = new Intl.Collator(undefined, {
  sensitivity: 'base',
  numeric: true,
})

const isEmpty = (value: TableSortValue) =>
  value === undefined || value === null || value === ''

const compareValues = (a: TableSortValue, b: TableSortValue) =>
  typeof a === 'string' && typeof b === 'string'
    ? collator.compare(a, b)
    : Number(a) - Number(b)

// Rows open with Enter or Space too, but not when the key press comes from a
// button or link inside the row
const activateProps = (onActivate?: () => void) =>
  onActivate && {
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (
        e.target !== e.currentTarget ||
        (e.key !== 'Enter' && e.key !== ' ')
      ) {
        return
      }
      e.preventDefault()
      onActivate()
    },
  }

const Table: React.FC<IProps> = ({
  className,
  mainHeaders,
  mainData,
  columnWidths,
  defaultSort,
  headerCaptions,
  variant,
  compact,
  allColumnsOnMobile,
  loading,
  emptyState,
}) => {
  const { theme } = useAppState()
  const { navOffset } = useStickyNavOffset()
  const tableRef = useRef<HTMLTableElement>(null)
  const headRef = useRef<HTMLTableSectionElement>(null)
  const [canStick, setCanStick] = useState(false)
  const [stuck, setStuck] = useState(false)

  // Only a table taller than the room under the sticky bars gets a sticky
  // header; it is stuck once it reaches that offset while the page scrolls
  useEffect(() => {
    const table = tableRef.current
    const head = headRef.current
    if (!table || !head) return undefined
    const update = () => {
      const { position, top: topValue } = window.getComputedStyle(head)
      const top = parseFloat(topValue) || 0
      const tall = table.offsetHeight > window.innerHeight - top
      setCanStick(tall)
      setStuck(
        tall &&
          position === 'sticky' &&
          head.getBoundingClientRect().top <= top + 1
      )
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(update)
    observer?.observe(table)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      observer?.disconnect()
    }
  }, [])
  const [sort, setSort] = useState(defaultSort)
  const [openRows, setOpenRows] = useState<Record<string, boolean>>(() =>
    mainData.reduce(
      (open, data, index) => ({
        ...open,
        [data.id ?? index]: !!data.openByDefault,
      }),
      {}
    )
  )

  const headers: ITableHeader[] = mainHeaders.map((header) =>
    typeof header === 'string' ? { label: header } : header
  )
  // Icons sit on the right: a trailing column without a header, or a column
  // marked align 'end'. Every other column is left-aligned
  const alignEnd = (index: number, count: number) =>
    headers[index]?.align === 'end' ||
    (index === count - 1 && !headers[index]?.label)

  const rows = mainData.map((data, index) => ({
    data,
    key: data.id ?? `${index}`,
  }))
  if (sort) {
    const sign = sort.direction === 'asc' ? 1 : -1
    const valueOf = (row: typeof rows[number]) =>
      row.data.sortValues?.[sort.column]
    rows.sort((a, b) => {
      const [x, y] = [valueOf(a), valueOf(b)]
      if (isEmpty(x) || isEmpty(y)) {
        return Number(isEmpty(x)) - Number(isEmpty(y))
      }
      return compareValues(x, y) * sign
    })
  }

  const empty = !loading && mainData.length === 0 ? emptyState : undefined

  const sortBy = (column: number, type: TableSortType) =>
    setSort((prev) =>
      prev?.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: type === 'text' ? 'asc' : 'desc' }
    )

  return (
    <Container
      ref={tableRef}
      className={className}
      grouped={variant === 'grouped'}
      compact={compact}
      allColumnsOnMobile={allColumnsOnMobile}
      aria-busy={loading || undefined}
    >
      {headers.length > 0 && !empty?.noData && (
        <StickyHead
          ref={headRef}
          navOffset={navOffset}
          canStick={canStick}
          stuck={stuck}
        >
          <tr>
            {headers.map(({ label, sort: columnSort }, headerIndex) => {
              // Nothing to sort until the rows are in
              const sortType = loading ? undefined : columnSort
              const direction =
                sort?.column === headerIndex ? sort.direction : undefined
              return (
                <MainHeader
                  key={`table-main-header-${headerIndex}`}
                  width={columnWidths?.[headerIndex]}
                  alignEnd={alignEnd(headerIndex, headers.length)}
                  aria-sort={
                    sortType &&
                    (direction === 'asc'
                      ? 'ascending'
                      : direction === 'desc'
                      ? 'descending'
                      : 'none')
                  }
                >
                  {sortType ? (
                    <SortButton
                      type="button"
                      active={!!direction}
                      onClick={() => sortBy(headerIndex, sortType)}
                    >
                      {label}
                      <Icon
                        type="Sort"
                        size={12}
                        direction={
                          direction && (direction === 'asc' ? 'up' : 'down')
                        }
                        color={
                          direction
                            ? theme.colors.primary
                            : theme.colors.textLight
                        }
                      />
                    </SortButton>
                  ) : (
                    label
                  )}
                </MainHeader>
              )
            })}
          </tr>
          {headerCaptions && (
            <CaptionRow>
              {headerCaptions.map((caption, captionIndex) => (
                <CaptionCell
                  key={`table-caption-${captionIndex}`}
                  alignEnd={alignEnd(captionIndex, headerCaptions.length)}
                >
                  {caption}
                </CaptionCell>
              ))}
            </CaptionRow>
          )}
        </StickyHead>
      )}
      {loading &&
        Array.from({ length: 5 }, (_, rowIndex) => (
          <tbody key={`table-skeleton-${rowIndex}`} aria-hidden>
            <MainRow clickable={false}>
              {headers.map(({ label, sort: columnSort }, headerIndex) => (
                <MainEl
                  key={`table-skeleton-el-${headerIndex}`}
                  alignEnd={alignEnd(headerIndex, headers.length)}
                >
                  {headerIndex === 0 ? (
                    <SkeletonPerson>
                      <SkeletonCircle />
                      <SkeletonLines>
                        <SkeletonBar barWidth="80%" />
                        <SkeletonBar barWidth="50%" short />
                      </SkeletonLines>
                    </SkeletonPerson>
                  ) : (
                    label && (
                      <SkeletonBar
                        barWidth={columnSort === 'number' ? '32px' : '72px'}
                      />
                    )
                  )}
                </MainEl>
              ))}
            </MainRow>
          </tbody>
        ))}
      {empty && (
        <tbody>
          <tr>
            <EmptyCell colSpan={Math.max(headers.length, 1)}>
              <EmptyContent>
                <EmptyIcon>
                  <Icon
                    type={empty.icon ?? (empty.noData ? 'List' : 'Search')}
                    size={24}
                    color={theme.colors.primary}
                  />
                </EmptyIcon>
                <Text textStyle="labelHeavy" textAlign="center">
                  {empty.title}
                </Text>
                {empty.text && (
                  <Text textStyle="label" color="textLight" textAlign="center">
                    {empty.text}
                  </Text>
                )}
                {empty.action && (
                  <EmptyAction
                    colorVariant="secondary"
                    caption={empty.action.caption}
                    onClick={empty.action.onClick}
                  />
                )}
              </EmptyContent>
            </EmptyCell>
          </tr>
        </tbody>
      )}
      {!loading &&
        rows.map(({ data, key }) => {
          const open = !!openRows[key]
          const expandable = !!data.subTable
          return (
            <tbody key={`table-body-${key}`}>
              <MainRow
                clickable={!!(data.subTable || data.onClick)}
                hoverColor={data.hoverColor}
                aria-expanded={expandable ? open : undefined}
                {...activateProps(
                  data.onClick || data.subTable
                    ? () => {
                        data.onClick && data.onClick()
                        setOpenRows((prev) => ({ ...prev, [key]: !prev[key] }))
                      }
                    : undefined
                )}
              >
                {data.elements.map((el, elIndex) => (
                  <MainEl
                    key={`table-main-el-${elIndex}`}
                    alignEnd={alignEnd(elIndex, data.elements.length)}
                  >
                    {data.subTable &&
                    !data.subTable.customExpandIcon &&
                    elIndex === data.elements.length - 1 ? (
                      <Icon
                        size={12}
                        type={open ? 'ChevronUp' : 'ChevronDown'}
                        color={
                          open
                            ? theme.colors.primary
                            : theme.colors.primaryFadeLight
                        }
                      />
                    ) : (
                      el
                    )}
                  </MainEl>
                ))}
              </MainRow>
              {data.subTable && open && (
                <React.Fragment>
                  <SubHeaderRow>
                    {data.subTable.subHeaders.map(
                      (subHeader, subHeaderIndex) => (
                        <SubHeader
                          key={`table-sub-header-${subHeaderIndex}`}
                          alignEnd={alignEnd(
                            subHeaderIndex,
                            data.subTable?.subHeaders.length ?? 0
                          )}
                        >
                          {subHeader}
                        </SubHeader>
                      )
                    )}
                  </SubHeaderRow>
                  {data.subTable.subElements.map((subEl, subElIndex) => (
                    <SubElRow
                      key={`table-sub-row-${subElIndex}`}
                      data-cy={subEl['data-cy']}
                      clickable={!!subEl.onClick}
                      {...activateProps(subEl.onClick)}
                    >
                      {subEl.data.map((subData, subDataIndex) => (
                        <SubEl
                          key={`table-sub-el-${subDataIndex}`}
                          alignEnd={alignEnd(subDataIndex, subEl.data.length)}
                        >
                          {subData}
                        </SubEl>
                      ))}
                    </SubElRow>
                  ))}
                </React.Fragment>
              )}
            </tbody>
          )
        })}
    </Container>
  )
}

export default Table

// Shared by every cell: 16px gutters on the outer edges, the icon column on the
// right, and below M only the first and last column stay visible
const cellLayout = (alignEnd: boolean) => `
  text-align: ${alignEnd ? 'right' : 'left'};

  &:first-of-type {
    padding-left: 16px;
  }

  &:last-of-type {
    padding-right: 16px;
  }
`

const mobileFirstAndLast = mqTo.M`
  display: none;

  &:first-of-type,
  &:last-of-type {
    display: table-cell;
  }
`

// The header sticks 8px under the filter panel when a page has one (it
// publishes --sticky-top), otherwise under the navigation. Dialogs and layovers
// scroll on their own and switch it off with --table-header-position. Once
// stuck, a page-coloured band fills the 8px above it so rows don't show through,
// and a line closes it off below
const StickyHead = styled.thead<{
  navOffset: number
  canStick: boolean
  stuck: boolean
}>`
  position: ${({ canStick }) =>
    canStick ? 'var(--table-header-position, sticky)' : 'static'};
  top: calc(var(--sticky-top, ${({ navOffset }) => navOffset}px) + 8px);
  z-index: 2;

  ${({ stuck, theme }) =>
    stuck &&
    css`
      // The band sits behind the header cells and reaches past their rounded
      // corners, so the corners stay round without rows showing through. It
      // runs 4px wider on each side to hide the table's outline above the header
      > tr:first-child > th {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          z-index: -1;
          top: -8px;
          left: -4px;
          right: -4px;
          height: 20px;
          background-color: ${theme.colors.primaryLight};
        }
      }

      > tr:last-child > * {
        background-image: linear-gradient(
          to top,
          ${theme.colors.textLighter} 1px,
          transparent 1px
        );
      }
    `}
`

const MainHeader = styled.th<{ width?: string; alignEnd: boolean }>`
  ${applyTextStyle('labelSmallCaps')};
  background-color: ${({ theme }) => theme.colors.lightest};
  color: ${({ theme }) => theme.colors.textLight};
  padding: 8px 0;
  ${({ width }) => width && `width: ${width};`}
  ${({ alignEnd }) => cellLayout(alignEnd)}

  &[aria-sort='ascending'],
  &[aria-sort='descending'] {
    color: ${({ theme }) => theme.colors.primary};
  }

  ${mqTo.M`
    display: none;

    &:first-of-type {
      display: table-cell;
      width: 90%;
    }

    &:last-of-type {
      display: table-cell;
      width: 10%;
    }
  `}
`

// Inline, not flex: a label that wraps (a long company term) keeps the caret
// right after its last word instead of pushing it to the cell's edge
const SortButton = styled.button<{ active: boolean }>`
  display: inline;
  text-align: inherit;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  cursor: pointer;
  transition: color ${fastTransition};

  ${IconWrapper} {
    margin-left: 4px;
    /* Optical: centres the 12px caret on the caps of the label (cap height
       ~8px), where 'middle' centres on the x-height and drops it */
    vertical-align: -2px;
  }

  ${({ active, theme }) =>
    !active &&
    `
    &:hover {
      color: ${theme.colors.text};

      path {
        fill: ${theme.colors.textLight};
      }
    }
  `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

// On hover the trailing icon turns primary, and counters and buttons go white
// so they stay distinct from the hover tint
const clickableRow = (hoverColor: string, theme: ITheme) => `
  cursor: pointer;
  transition: background-color ${fastTransition};

  &:hover {
    background-color: ${hoverColor};

    strong,
    > td:last-child button {
      background-color: ${theme.colors.lightest};
    }

    > td:last-child svg path {
      fill: ${theme.colors.primary};
    }
  }
`

export const MainRow = styled.tr<{ clickable: boolean; hoverColor?: string }>`
  ${({ clickable, hoverColor, theme }) =>
    clickable &&
    clickableRow(hoverColor || theme.colors.primaryFadeLight, theme)}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`

const MainEl = styled.td<{ alignEnd?: boolean }>`
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.textLightest};
  ${({ alignEnd }) => cellLayout(!!alignEnd)}
  ${mobileFirstAndLast}
`

const SubHeaderRow = styled.tr`
  background-color: ${({ theme }) => theme.colors.textLightest};
`

const SubHeader = styled.th<{ alignEnd: boolean }>`
  ${applyTextStyle('labelSmallCaps')};
  color: ${({ theme }) => theme.colors.textLight};
  padding: 8px 0;
  ${({ alignEnd }) => cellLayout(alignEnd)}
  ${mobileFirstAndLast}
`

const SubElRow = styled.tr<{ clickable: boolean }>`
  ${({ clickable, theme }) =>
    clickable && clickableRow(theme.colors.primaryFadeLight, theme)}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`

const SubEl = styled.td<{ alignEnd?: boolean }>`
  padding: 24px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.textLightest};
  background: ${({ theme }) => hsla(theme.colors.textLightest, 0.45)};
  ${({ alignEnd }) => cellLayout(!!alignEnd)}
  ${mobileFirstAndLast}
`

const CaptionRow = styled.tr`
  background-color: ${({ theme }) => theme.colors.primaryLight};
`

const CaptionCell = styled.td<{ alignEnd: boolean }>`
  padding: 4px 0;
  ${({ alignEnd }) => cellLayout(alignEnd)}
  ${mobileFirstAndLast}
`

// The empty state sits in one cell across every column, so the table keeps its
// card and headers instead of the page jumping to a loose line of text
const EmptyCell = styled.td`
  padding: 48px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.textLightest};
`

const EmptyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

const EmptyIcon = styled.span`
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  margin-bottom: 4px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primaryLight};
`

const EmptyAction = styled(Button)`
  margin-top: 8px;
`

const shimmer = keyframes`
  from {
    background-position: 200% 0;
  }

  to {
    background-position: -200% 0;
  }
`

const SkeletonBar = styled.span<{ barWidth: string; short?: boolean }>`
  display: block;
  width: ${({ barWidth }) => barWidth};
  height: ${({ short }) => (short ? 8 : 12)}px;
  margin-top: ${({ short }) => (short ? 8 : 0)}px;
  border-radius: 999px;
  background: ${({ theme }) =>
    `linear-gradient(90deg, ${theme.colors.textLightest} 0%, ${hsla(
      theme.colors.textLightest,
      0.4
    )} 50%, ${theme.colors.textLightest} 100%)`};
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const SkeletonCircle = styled(SkeletonBar).attrs({ barWidth: '32px' })`
  flex: none;
  height: 32px;
  border-radius: 50%;
`

const SkeletonPerson = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const SkeletonLines = styled.div`
  flex: 1;
  max-width: 160px;
`

// No overflow: hidden, so menus inside rows are never clipped; the corner
// cells carry the rounding instead
const Container = styled.table<{
  grouped: boolean
  compact?: boolean
  allColumnsOnMobile?: boolean
}>`
  border-radius: 12px;
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0px;
  background-color: ${({ theme }) => theme.colors.lightest};
  box-shadow: ${shadows.depthTwo};

  > thead > tr:first-child > th:first-child,
  > tbody:first-child > tr:first-child > td:first-child {
    border-top-left-radius: 12px;
  }

  > thead > tr:first-child > th:last-child,
  > tbody:first-child > tr:first-child > td:last-child {
    border-top-right-radius: 12px;
  }

  > tbody:last-child > tr:last-child > td:first-child {
    border-bottom-left-radius: 12px;
  }

  > tbody:last-child > tr:last-child > td:last-child {
    border-bottom-right-radius: 12px;
  }

  /* Without a header the first row needs no divider above it */
  > tbody:first-child > tr:first-child > td {
    border-top: none;
  }

  ${({ grouped, theme }) =>
    grouped &&
    css`
      ${MainRow}:not(:hover) {
        background-color: ${theme.colors.primaryLight};
      }

      ${SubElRow}:not(:hover) {
        background-color: ${theme.colors.lightest};
      }

      ${SubEl} {
        background: none;
      }
    `}

  ${({ compact }) =>
    compact &&
    css`
      ${MainEl} {
        padding-top: 8px;
        padding-bottom: 8px;
      }

      ${SubEl} {
        padding-top: 12px;
        padding-bottom: 12px;
      }
    `}

  ${({ allColumnsOnMobile }) =>
    allColumnsOnMobile &&
    mqTo.M`
      th,
      td {
        display: table-cell;
      }
    `}
`
